/**
 * Content checks — V117-3-3 (ADR-0112 §1–§3; D-116-5, D-116-6).
 *
 * Layer 1 of the remediation-context contract. A control spec may carry `content_checks_json` (migration 1018):
 *   required_sections[]    {key, title, aliases?[], provisional?}: the record lists a section with that key, or one
 *                          whose key / title matches the title or an alias (case- and spacing-insensitive)
 *   requires_named_owner   the record names an owner (a member, or a name)
 *   requires_signature     approver + time + the SHA-256 of the exact artifact approved (§3); a later artifact hash
 *                          that differs lapses it (hash_mismatch)
 *   review_period_days     the evidence was reviewed or approved within this many days
 *   required_parameters[]  {key, type, op, value?, provisional?}, e.g. disclosure_interval_hours <= 3
 *   publication_required   a public https URL is recorded and resolved (HTTP 200) at least once
 * A scalar check is written `true` / a number, or `{value, provisional: true}`; list items carry `provisional`
 * themselves. A provisional check (v11.6's draft-standard checks) is evaluated and reported, marked, and never
 * decides the verdict.
 *
 * PURE and deterministic: the only input is a CONFIRMED content record (evidence_content_records, migration 1021).
 * An LLM may propose record fields (V117-3-4b) and flag documents, but never passes a control: pass / fail is decided
 * here, so an assessor reproduces every verdict from the record and the artifact. `gac fulfill` output is judged by
 * the same evaluator. A spec with `{}` has no checks: `not_applicable`, and the control behaves exactly as before.
 *
 * Each check has a stable key (`section:<key>`, `named_owner`, `signature`, `review_period`, `parameter:<key>`,
 * `publication`); a control guide's machine acceptance criterion names one (`check_ref`, ADR-0112 §6).
 */
import { z } from 'zod';
// ============================================================
// Schema
// ============================================================
const flag = z.union([z.boolean(), z.object({ value: z.boolean(), provisional: z.boolean().optional() }).strict()]);
const days = z.union([
    z.number().int().positive(),
    z.object({ value: z.number().int().positive(), provisional: z.boolean().optional() }).strict(),
]);
const KEY = /^[a-z][a-z0-9_]{0,63}$/;
export const PARAMETER_OPS = ['<=', '<', '>=', '>', '==', '!=', 'present'];
const sectionSchema = z.object({
    key: z.string().regex(KEY),
    title: z.string().min(1).max(200),
    aliases: z.array(z.string().min(1).max(200)).max(20).optional(),
    provisional: z.boolean().optional(),
}).strict();
const parameterSchema = z.object({
    key: z.string().regex(KEY),
    type: z.enum(['number', 'string', 'boolean']),
    op: z.enum(PARAMETER_OPS),
    value: z.union([z.number(), z.string(), z.boolean()]).optional(),
    unit: z.string().max(40).optional(),
    provisional: z.boolean().optional(),
}).strict().superRefine((p, ctx) => {
    if (p.op === 'present')
        return;
    // No value: it comes from the strictest active obligation (ADR-0112 §4, effective-spec-service); until one supplies
    // it, the check is a presence check.
    if (p.value !== undefined && typeof p.value !== p.type)
        ctx.addIssue({ code: 'custom', message: `parameter ${p.key}: value is not a ${p.type}` });
    if (p.type !== 'number' && ['<=', '<', '>=', '>'].includes(p.op))
        ctx.addIssue({ code: 'custom', message: `parameter ${p.key}: op ${p.op} needs a number` });
});
export const contentChecksSchema = z.object({
    required_sections: z.array(sectionSchema).max(50).optional(),
    requires_named_owner: flag.optional(),
    requires_signature: flag.optional(),
    review_period_days: days.optional(),
    required_parameters: z.array(parameterSchema).max(50).optional(),
    publication_required: flag.optional(),
}).strict().superRefine((c, ctx) => {
    const dup = (keys, what) => {
        const seen = new Set();
        for (const k of keys) {
            if (seen.has(k))
                ctx.addIssue({ code: 'custom', message: `duplicate ${what} key ${k}` });
            seen.add(k);
        }
    };
    dup((c.required_sections ?? []).map((s) => s.key), 'section');
    dup((c.required_parameters ?? []).map((p) => p.key), 'parameter');
});
/** Parse and validate a spec's content checks; `{}` / null / '' → no checks. Throws with every problem named. */
export function parseContentChecks(input) {
    let value = input;
    if (typeof value === 'string') {
        if (value.trim() === '')
            return {};
        try {
            value = JSON.parse(value);
        }
        catch {
            throw new Error('content_checks_json is not valid JSON');
        }
    }
    if (value === null || value === undefined)
        return {};
    const r = contentChecksSchema.safeParse(value);
    if (!r.success)
        throw new Error(`Invalid content checks: ${r.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`).join('; ')}`);
    return r.data;
}
/** True when a spec carries at least one content check. */
export function hasContentChecks(checks) {
    if (!checks)
        return false;
    return Boolean((checks.required_sections?.length ?? 0) > 0 || (checks.required_parameters?.length ?? 0) > 0
        || flagOn(checks.requires_named_owner) || flagOn(checks.requires_signature) || flagOn(checks.publication_required)
        || daysOf(checks.review_period_days) !== null);
}
const flagOn = (f) => (typeof f === 'object' && f !== null ? f.value : f === true);
const flagProvisional = (f) => typeof f === 'object' && f !== null && f.provisional === true;
const daysOf = (d) => (d === undefined ? null : typeof d === 'number' ? d : d.value);
/** The stable keys of every check a spec declares — what a guide's machine criterion may name (`check_ref`). */
export function contentCheckKeys(checks) {
    if (!checks)
        return [];
    const keys = [];
    for (const s of checks.required_sections ?? [])
        keys.push(`section:${s.key}`);
    if (flagOn(checks.requires_named_owner))
        keys.push('named_owner');
    if (flagOn(checks.requires_signature))
        keys.push('signature');
    if (daysOf(checks.review_period_days) !== null)
        keys.push('review_period');
    for (const p of checks.required_parameters ?? [])
        keys.push(`parameter:${p.key}`);
    if (flagOn(checks.publication_required))
        keys.push('publication');
    return keys;
}
/** A row of evidence_content_records → the record the evaluator reads. Malformed JSON reads as empty. */
export function contentRecordFromRow(row) {
    const json = (v, fallback) => {
        if (typeof v !== 'string' || v === '')
            return fallback;
        try {
            return JSON.parse(v);
        }
        catch {
            return fallback;
        }
    };
    const str = (v) => (typeof v === 'string' && v !== '' ? v : null);
    const sections = json(row.sections_json, []);
    const params = json(row.parameters_json, {});
    const approver = str(row.approval_approver);
    const approvedAt = str(row.approval_approved_at);
    const approvedSha = str(row.approval_artifact_sha256);
    return {
        record_id: String(row.record_id ?? ''),
        status: (row.status === 'confirmed' || row.status === 'superseded' ? row.status : 'draft'),
        artifact_id: str(row.artifact_id),
        artifact_sha256: str(row.artifact_sha256),
        control_id: str(row.control_id),
        system_id: str(row.system_id),
        evidence_kind: str(row.evidence_kind),
        sections: Array.isArray(sections) ? sections.filter((s) => s && typeof s === 'object' && typeof s.key === 'string') : [],
        owner_user_id: str(row.owner_user_id),
        owner_name: str(row.owner_name),
        approval: approver && approvedAt && approvedSha ? { approver, approved_at: approvedAt, artifact_sha256: approvedSha } : null,
        reviewed_at: str(row.reviewed_at),
        parameters: params && typeof params === 'object' && !Array.isArray(params) ? params : {},
        publication_url: str(row.publication_url),
        publication_resolved_at: str(row.publication_resolved_at),
        confirmed_by: str(row.confirmed_by),
        confirmed_at: str(row.confirmed_at),
    };
}
const DAY_MS = 86_400_000;
const norm = (s) => s.toLowerCase().replace(/[\s_\-–—:.()]+/g, ' ').trim();
const parseTime = (x) => Date.parse(x.includes('T') ? x : `${x.replace(' ', 'T')}Z`);
/** Does the record list this section (by key, or a key / title matching the title or an alias)? */
export function sectionPresent(record, s) {
    const names = new Set([s.title, ...(s.aliases ?? [])].map(norm));
    return record.sections.some((r) => r.key === s.key || names.has(norm(r.key)) || (r.title ? names.has(norm(r.title)) : false));
}
/**
 * Is the signature intact? Approver + time + artifact sha, and the sha still matches the artifact. With no artifact
 * hash to compare (a form-only record, no current artifact) the approval pins nothing: `missing_pin`, never a pass
 * (review #464 M4).
 */
export function signatureState(record, currentArtifactSha256) {
    if (!record.approval)
        return 'missing';
    const current = currentArtifactSha256 ?? record.artifact_sha256;
    if (!current)
        return 'missing_pin';
    if (current.toLowerCase() !== record.approval.artifact_sha256.toLowerCase())
        return 'hash_mismatch';
    return 'valid';
}
/** The missing item a signature state names (shared by the content check and the signed_at floor). */
export function signatureGap(sig) {
    switch (sig) {
        case 'valid': return null;
        case 'hash_mismatch': return 'A signature on the current version (the document changed after it was approved)';
        case 'missing_pin': return 'A signature pinned to the approved document (this record has no document version to pin it to)';
        case 'missing': return 'A signature: approver, date and the approved version';
    }
}
/** The latest of reviewed_at and the approval time, or null. */
export function lastReviewedAt(record) {
    const times = [record.reviewed_at, record.approval?.approved_at].filter((t) => !!t && Number.isFinite(parseTime(t)));
    if (!times.length)
        return null;
    return times.reduce((a, b) => (parseTime(a) >= parseTime(b) ? a : b));
}
function compare(op, actual, expected) {
    if (expected === undefined)
        return true; // no value supplied by the spec or an obligation: presence only
    switch (op) {
        case 'present': return true;
        case '==': return actual === expected;
        case '!=': return actual !== expected;
        case '<=': return typeof actual === 'number' && typeof expected === 'number' && actual <= expected;
        case '<': return typeof actual === 'number' && typeof expected === 'number' && actual < expected;
        case '>=': return typeof actual === 'number' && typeof expected === 'number' && actual >= expected;
        case '>': return typeof actual === 'number' && typeof expected === 'number' && actual > expected;
    }
}
function describeParameter(p) {
    if (p.op === 'present' || p.value === undefined)
        return `Parameter ${p.key}`;
    return `Parameter ${p.key} ${p.op} ${String(p.value)}${p.unit ? ` ${p.unit}` : ''}`;
}
/**
 * Evaluate a spec's content checks against a content record. Only a CONFIRMED record counts: a draft (or a
 * superseded record) is no record. Deterministic: the same checks, record, artifact hash and day give the same result.
 */
export function evaluateContentChecks(checks, record, opts = {}) {
    if (!hasContentChecks(checks))
        return { verdict: 'not_applicable', results: [], missing: [], provisional_missing: [] };
    const c = checks;
    if (!record || record.status !== 'confirmed') {
        return { verdict: 'no_record', results: [], missing: ['A confirmed content record for this evidence'], provisional_missing: [] };
    }
    const now = (opts.now ?? new Date()).getTime();
    const results = [];
    const push = (key, kind, passed, provisional, missing) => results.push({ key, kind, passed, provisional, missing: passed ? null : missing });
    for (const s of c.required_sections ?? []) {
        push(`section:${s.key}`, 'section', sectionPresent(record, s), s.provisional === true, `Section “${s.title}”`);
    }
    if (flagOn(c.requires_named_owner)) {
        push('named_owner', 'named_owner', Boolean(record.owner_user_id || record.owner_name?.trim()), flagProvisional(c.requires_named_owner), 'A named owner');
    }
    if (flagOn(c.requires_signature)) {
        const sig = signatureState(record, opts.currentArtifactSha256);
        push('signature', 'signature', sig === 'valid', flagProvisional(c.requires_signature), signatureGap(sig) ?? '');
    }
    const period = daysOf(c.review_period_days);
    if (period !== null) {
        const last = lastReviewedAt(record);
        const ok = last !== null && now - parseTime(last) <= period * DAY_MS;
        push('review_period', 'review_period', ok, typeof c.review_period_days === 'object' && c.review_period_days.provisional === true, last ? `A review or approval within the last ${period} days (last: ${last.slice(0, 10)})` : `A review or approval date within the last ${period} days`);
    }
    for (const p of c.required_parameters ?? []) {
        const got = record.parameters[p.key];
        const value = got?.value;
        const typed = value !== undefined && value !== null && typeof value === p.type;
        const ok = typed && compare(p.op, value, p.value);
        push(`parameter:${p.key}`, 'parameter', ok, p.provisional === true, `${describeParameter(p)}${value !== undefined && value !== null ? ` (recorded: ${String(value)})` : ''}`);
    }
    if (flagOn(c.publication_required)) {
        const url = record.publication_url;
        const ok = !!url && /^https:\/\//i.test(url) && !!record.publication_resolved_at;
        push('publication', 'publication', ok, flagProvisional(c.publication_required), url ? 'The public URL resolving (HTTP 200) at least once' : 'A public https URL where it is published');
    }
    const missing = results.filter((r) => !r.passed && !r.provisional).map((r) => r.missing);
    const provisionalMissing = results.filter((r) => !r.passed && r.provisional).map((r) => r.missing);
    return { verdict: missing.length ? 'fail' : 'pass', results, missing, provisional_missing: provisionalMissing };
}
//# sourceMappingURL=content-check-service.js.map